/**
 * Farmer Lifecycle Step Definitions & Computation
 *
 * Separates all lifecycle logic from the UI so it can be
 * tested independently and reused across components.
 */

/**
 * Ordered lifecycle steps (1-indexed for display).
 * Override via the `stepsData` prop on <LifecycleStepper />.
 */
export const LIFECYCLE_STEPS = [
  { step: 1,  key: 'onboarded',           label: 'Onboarded' },
  { step: 2,  key: 'kyc_verified',        label: 'KYC Verified' },
  { step: 3,  key: 'farm_created',        label: 'Farm Created' },
  { step: 4,  key: 'pond_created',        label: 'Pond Created' },
  { step: 5,  key: 'ask_for_credit',      label: 'Ask for Credit' },
  { step: 6,  key: 'credit_active',       label: 'Credit Active' },
  { step: 7,  key: 'stocking',            label: 'Stocking' },
  { step: 8,  key: 'policy_active',       label: 'Policy Active' },
  { step: 9,  key: 'crop_active',         label: 'Crop Active' },
  { step: 10, key: 'harvest',             label: 'Harvest' },
  { step: 11, key: 'settlement_complete', label: 'Settlement Complete' },
];

/* ------------------------------------------------------------------ */
/*  Status helpers (case-insensitive matching)                        */
/* ------------------------------------------------------------------ */

const eq = (value, ...targets) => {
  if (!value) return false;
  const v = String(value).toLowerCase().trim();
  return targets.some((t) => v === t.toLowerCase());
};

const hasAny = (arr) => Array.isArray(arr) && arr.length > 0;

/* ------------------------------------------------------------------ */
/*  computeLifecycleStep                                              */
/* ------------------------------------------------------------------ */

/**
 * Derive the current lifecycle step (1 – 11) from user / entity data.
 *
 * Each argument is optional; the function walks forward through the
 * steps and returns the highest step whose prerequisite is satisfied.
 *
 * @param {Object} userData
 * @param {Object}   [userData.user]               – User record
 * @param {string}   [userData.user.kyc_status]     – 'approved' | 'verified' | …
 * @param {Array}    [userData.farms]               – List of farms
 * @param {Array}    [userData.ponds]               – List of ponds
 * @param {Object}   [userData.credit]              – Credit application
 * @param {string}   [userData.credit.status]       – 'pending' | 'approved' | 'disbursed' | …
 * @param {Object}   [userData.stocking]            – Stocking event
 * @param {boolean}  [userData.stocking.exists]     – true if stocking exists
 * @param {Object}   [userData.policy]              – Policy record
 * @param {string}   [userData.policy.status]       – 'active' | 'pending' | …
 * @param {Object}   [userData.pond]                – Active pond
 * @param {string}   [userData.pond.status]         – 'in_crop' | 'active' | …
 * @param {Object}   [userData.harvest]             – Harvest record
 * @param {boolean}  [userData.harvest.exists]       – true if harvest recorded
 * @param {Object}   [userData.settlement]          – Settlement record
 * @param {string}   [userData.settlement.status]   – 'completed' | 'approved' | …
 * @returns {number} Step number (1 – 11)
 */
export const computeLifecycleStep = (userData = {}) => {
  const {
    user       = {},
    farms      = [],
    ponds      = [],
    credit     = {},
    stocking   = {},
    policy     = {},
    pond       = {},
    harvest    = {},
    settlement = {},
  } = userData;

  // Step 11 → Settlement Complete
  if (eq(settlement.status, 'completed')) return 11;

  // Step 10 → Harvest recorded
  if (harvest.exists) return 10;

  // Step 9 → Crop Active (pond in active crop cycle)
  if (eq(pond.status, 'in_crop', 'crop_active', 'active_crop')) return 9;

  // Step 8 → Policy Active
  if (eq(policy.status, 'active')) return 8;

  // Step 7 → Stocking event exists
  if (stocking.exists) return 7;

  // Step 6 → Credit approved / disbursed
  if (eq(credit.status, 'approved', 'disbursed', 'active')) return 6;

  // Step 5 → Credit applied (any status present)
  if (
    credit.status &&
    eq(credit.status, 'pending', 'under_review', 'submitted', 'approved', 'disbursed', 'active')
  ) return 5;

  // Step 4 → Pond created
  if (hasAny(ponds)) return 4;

  // Step 3 → Farm created
  if (hasAny(farms)) return 3;

  // Step 2 → KYC Verified
  if (eq(user.kyc_status, 'approved', 'verified')) return 2;

  // Step 1 → Onboarded (default — user exists)
  return 1;
};

/**
 * Return descriptive metadata for a given step number.
 *
 * @param {number} stepNumber  1-based step index
 * @param {Array}  [steps]     Custom step definitions (defaults to LIFECYCLE_STEPS)
 * @returns {{ key: string, label: string, step: number } | undefined}
 */
export const getStepMeta = (stepNumber, steps = LIFECYCLE_STEPS) =>
  steps.find((s) => s.step === stepNumber);
